import React from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom"; // Use 'Routes' instead of 'Switch' for React Router v6
import Ingredients from "./components/IngredientsPage";
import Directions from "./components/DirectionsPage";
import Specials from "./components/SpecialsPage";
import Home from "./components/Home";
import { store, persistor } from "./state/store";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";

// 1. Import createTheme and ThemeProvider from MUI v5, and ThemeProvider from @mui/styles for makeStyles compatibility
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from "@mui/material/styles";
import { ThemeProvider as StylesThemeProvider } from "@mui/styles";

// 2. Define a Material-UI Theme
// The theme is necessary to provide the 'theme' object to makeStyles/useStyles
const theme = createTheme({
  // Define spacing as a function so theme.spacing(...) is always valid
  spacing: (factor) => 4 * factor,
  // 🎨 Palette Customization
  palette: {
    primary: {
      // Set a primary color (e.g., a nice blue for buttons and app bar)
      main: "#0070f3",
      light: "#3392ff",
      dark: "#004aab",
      contrastText: "#ffffff", // Ensures text on primary elements is white
    },
    secondary: {
      // Set a secondary color (e.g., for accents or specialized buttons)
      main: "#FF4081",
      light: "#ff79b0",
      dark: "#c50055",
      contrastText: "#000000", // Ensures text on secondary elements is black
    },
    background: {
      // Light gray background for better visual separation
      default: "#f7f9fc",
      paper: "#ffffff",
    },
    // Optional: Define a custom color like 'success', 'warning', etc.
    // Or customize text/error/info colors.
  },

  // 📝 Typography Customization
  typography: {
    fontFamily: [
      "Roboto", // Default fallback
      "Helvetica Neue",
      "Arial",
      "sans-serif",
    ].join(","),
    h6: {
      // Customize the size/weight for h6 (used in DialogTitle, App Bar)
      fontWeight: 600,
      fontSize: "1.25rem",
    },
    body2: {
      // Customize body text
      fontSize: "0.875rem",
      lineHeight: 1.5,
    },
    button: {
      // Customize button text (e.g., always uppercase)
      textTransform: "none",
      fontWeight: 500,
    },
  },
  // 🧩 Component Overrides (Mui-style components)
  components: {
    MuiButton: {
      defaultProps: {
        // Apply this prop to all MUI Buttons by default
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8, // Rounded buttons globally
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12, // More rounded cards
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)", // Slightly stronger shadow
        },
      },
    },
  },
});

const App = () => (
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      {/* 3. Wrap the application with both styles and MUI ThemeProviders so @mui/styles makeStyles receives the theme */}
      <StylesThemeProvider theme={theme}>
        <MuiThemeProvider theme={theme}>
          {/*
            4. NOTE: React Router v6 uses <Routes> instead of <Switch>. 
            If you are using v6, update the import and component name.
            I've updated it here for forward compatibility.
          */}
          <BrowserRouter>
            <Routes>
              <Route exact path="/" element={<Home />} />
              <Route
                exact
                path="/ingredients/:recipeId"
                element={<Ingredients />}
              />
              <Route
                exact
                path="/directions/:recipeId"
                element={<Directions />}
              />
              <Route exact path="/specials" element={<Specials />} />
            </Routes>
          </BrowserRouter>
        </MuiThemeProvider>
      </StylesThemeProvider>
    </PersistGate>
  </Provider>
);

export default App;
